/**
 * Mock sub-app — simulates SoraMynd alpha-uplift in telerehab mode.
 *
 * Real sgimacog-web/NFB-Webapp integration follows this exact protocol:
 *   URL params: ?telerehab=1&room={roomId}&token={subappToken}
 *   BroadcastChannel('tr-{room}'):
 *     receive: {type:'params_update'|'set_source'|'set_game'|'reward_set'|'start'|'pause'|'stop'}
 *     emit:    {type:'metric'|'impedance'|'raw_preview'|'state'|'event'}
 *
 * This page:
 *   - generates a synthetic alpha signal (sine + noise)
 *   - reacts to T_a threshold updates from BC → modulates a plane visual
 *   - emits metric every second
 *   - Case operates this in a separate window; CogniHome case parent
 *     bridges BC ↔ WebRTC DataChannel ↔ trainer DO WS.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DemoGuard, NotInProduction, isDemoHostAllowed } from "../components/DemoBanner.js";

interface Params {
  T_a?: number;
  threshold?: number;
}

export default function SoraDemoSubapp() {
  const [search] = useSearchParams();
  const room  = search.get("room");
  const isTr  = search.get("telerehab") === "1";

  const [alpha, setAlpha]   = useState(8.0);
  const [reward, setReward] = useState(0);
  const [params, setParams] = useState<Params>({ T_a: 12 });
  const [running, setRunning] = useState(true);
  const bcRef = useRef<BroadcastChannel | null>(null);

  // Set up BroadcastChannel for telerehab mode
  useEffect(() => {
    if (!isTr || !room) return;
    if (!isDemoHostAllowed()) return;            // never run synthetic loop in prod
    const bc = new BroadcastChannel(`tr-${room}`);
    bcRef.current = bc;

    bc.onmessage = (ev) => {
      const m = ev.data;
      if (m.type === "params_update") {
        setParams(p => ({ ...p, [m.key]: m.value }));
      }
      if (m.type === "start")  setRunning(true);
      if (m.type === "pause")  setRunning(false);
      if (m.type === "resume") setRunning(true);
      if (m.type === "stop")   setRunning(false);
    };

    // Announce ready (subapp_state forwarded over WS to trainer + audit)
    bc.postMessage({ type: "subapp_state", state: "ready", source: "soramynd-demo" });

    return () => bc.close();
  }, [isTr, room]);

  // Synthesize alpha signal + emit. Every metric carries demo:true so
  // metric_snapshots.demo=1 — reports filter these out by default.
  useEffect(() => {
    if (!running) return;
    if (!isDemoHostAllowed()) return;
    const t = setInterval(() => {
      const next = 8 + Math.sin(Date.now() / 800) * 4 + (Math.random() - 0.5) * 2;
      setAlpha(next);
      const T_a = params.T_a ?? 12;
      const rwd = Math.max(0, Math.min(1, (next - T_a + 5) / 10));
      setReward(rwd);

      const ts = Date.now();
      bcRef.current?.postMessage({ type: "metric", source: "eeg.alpha", value: next, ts, demo: true });
      bcRef.current?.postMessage({ type: "metric", source: "reward",    value: rwd,  ts, demo: true });
    }, 500);
    return () => clearInterval(t);
  }, [running, params.T_a]);

  // Visual: plane altitude follows reward
  const planeY = 280 - reward * 200;

  if (!isDemoHostAllowed()) return <NotInProduction subapp="SoraMynd" />;

  if (!isTr) {
    return (
      <main className="page page--auth">
        <div className="serial">SUB-APP DEMO</div>
        <h1>SoraMynd Demo</h1>
        <p className="muted" style={{ marginTop: "var(--s-3)" }}>
          這個頁面只在 <code>?telerehab=1&amp;room=&lt;id&gt;</code> 模式下啟用。
          在 CogniHome studio 開 session 才會被自動開啟。
        </p>
      </main>
    );
  }

  return (
    <DemoGuard subapp="SoraMynd · plane">
      {/* Top status strip */}
      <div style={{
        padding: "var(--s-3) var(--s-5)", display: "flex", justifyContent: "space-between",
        background: "rgba(0,0,0,0.4)", color: "var(--paper)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        flexShrink: 0,
      }}>
        <span style={{ letterSpacing: "0.1em" }}>SORAMYND · plane · TELEREHAB</span>
        <span>room {room?.slice(0, 12)}</span>
        <span style={{ color: running ? "var(--olive)" : "var(--warning)" }}>
          ● {running ? "running" : "paused"}
        </span>
      </div>

      {/* Game viewport */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden",
                    background: "linear-gradient(180deg, #f7d8b4 0%, #e9b489 60%, #a36a45 100%)" }}>
        <svg viewBox="0 0 800 350" preserveAspectRatio="xMidYMid slice"
             style={{ width: "100%", height: "100%" }}>
          {/* Sun */}
          <circle cx="640" cy="80" r="40" fill="#fbf6e6" opacity="0.9" />
          {/* Clouds */}
          <ellipse cx="120" cy="80" rx="50" ry="14" fill="white" opacity="0.5" />
          <ellipse cx="500" cy="220" rx="80" ry="18" fill="white" opacity="0.4" />
          {/* Plane */}
          <g transform={`translate(380, ${planeY}) rotate(${reward > 0.5 ? -8 : 4})`} fill="#1c1814">
            <path d="M0,0 L80,0 L100,10 L80,20 L0,20 Z" />
            <path d="M30,-22 L52,-22 L46,0 L36,0 Z" />
            <path d="M30,20 L52,20 L46,42 L36,42 Z" />
          </g>
          {/* Threshold line */}
          <line x1="0" y1={280 - ((params.T_a ?? 12) / 20) * 200} x2="800" y2={280 - ((params.T_a ?? 12) / 20) * 200}
                stroke="#c4471f" strokeWidth="1" strokeDasharray="6 6" opacity="0.5" />
        </svg>
      </div>

      {/* Bottom HUD (case sees this — minimal) */}
      <div style={{ padding: "var(--s-3) var(--s-5)", display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--s-5)",
                    fontFamily: "var(--mono)", fontSize: "var(--t-sm)",
                    background: "rgba(0,0,0,0.3)" }}>
        <div>
          <div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>alpha</div>
          <div style={{ fontSize: "var(--t-md)", color: "var(--paper)" }}>{alpha.toFixed(2)} <span style={{ fontSize: "var(--t-xs)", color: "var(--paper-deep)" }}>µV²</span></div>
        </div>
        <div>
          <div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>reward</div>
          <div style={{ fontSize: "var(--t-md)", color: "var(--rust)" }}>{reward.toFixed(2)}</div>
        </div>
        <div>
          <div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>T_a (from trainer)</div>
          <div style={{ fontSize: "var(--t-md)", color: "var(--paper)" }}>{(params.T_a ?? 12).toFixed(1)}</div>
        </div>
      </div>
    </DemoGuard>
  );
}
